import { CLAUDE_API, BUILTIN_TR } from '../constants';

const LANG_FULL: Record<string, string> = {
  es: 'Spanish (Español)',   fr: 'French (Français)',
  ar: 'Arabic (العربية)',    zh: 'Chinese Simplified (中文)',
  de: 'German (Deutsch)',    ml: 'Malayalam (മലയാളം)',
  it: 'Italian (Italiano)',  nl: 'Dutch (Nederlands)',
  tr: 'Turkish (Türkçe)',    pt: 'Portuguese (Português)',
  ja: 'Japanese (日本語)',   hi: 'Hindi (हिन्दी)',
  ru: 'Russian (Русский)',
};

// Correct model string — claude-sonnet-4-20250514 does not exist
const CLAUDE_MODEL = 'claude-sonnet-4-5';

export interface TrResult { translated: string; usedLLM: boolean; }
export interface TestResult { ok: boolean; error?: string; }

export async function translateMessage(text: string, lang: string, apiKey: string): Promise<TrResult> {
  if (!text.trim() || lang === 'en') return { translated: text, usedLLM: false };

  if (apiKey.trim().startsWith('sk-ant-')) {
    try {
      const res = await fetch(CLAUDE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey.trim(),
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: CLAUDE_MODEL,
          max_tokens: 500,
          messages: [{ role: 'user', content:
            `You are a translator for a logistics support team.\n` +
            `Translate the following English message to ${LANG_FULL[lang] ?? lang}.\n` +
            `Return ONLY the translation — no explanation, no quotes.\n\nMessage: ${text}` }],
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const t = (data?.content?.[0]?.text as string)?.trim();
        if (t) return { translated: t, usedLLM: true };
      }
    } catch (e) { console.warn('[Translation] LLM failed:', e); }
  }

  return { translated: BUILTIN_TR[lang]?.[text] ?? text, usedLLM: false };
}

export async function translateToEnglish(text: string, fromLang: string, apiKey: string): Promise<string> {
  if (!text.trim() || fromLang === 'en') return text;
  const res = await fetch(CLAUDE_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey.trim(),
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: CLAUDE_MODEL,
      max_tokens: 300,
      messages: [{ role: 'user', content:
        `Translate this message to English. Source language: ${fromLang}.\n` +
        `Return ONLY the English translation.\n\nMessage: ${text}` }],
    }),
  });
  if (!res.ok) throw new Error(`Claude API ${res.status}`);
  const data = await res.json();
  return (data?.content?.[0]?.text as string)?.trim() ?? text;
}

export async function testApiKey(key: string): Promise<TestResult> {
  const trimmed = key.trim();
  if (!trimmed) return { ok: false, error: 'Key is empty' };
  if (!trimmed.startsWith('sk-ant-')) return { ok: false, error: 'Key should start with sk-ant-…' };

  try {
    const res = await fetch(CLAUDE_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': trimmed,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({ model: CLAUDE_MODEL, max_tokens: 5, messages: [{ role: 'user', content: 'hi' }] }),
    });

    if (res.ok) return { ok: true };
    const body = await res.json().catch(() => null);
    const msg = body?.error?.message as string | undefined;
    if (res.status === 401) return { ok: false, error: 'Invalid key — authentication failed' };
    if (res.status === 403) return { ok: false, error: 'Key does not have permission' };
    if (res.status === 429) return { ok: false, error: 'Rate limited — try again in a moment' };
    return { ok: false, error: msg ?? `HTTP ${res.status}` };
  } catch {
    return { ok: false, error: 'Network error — check your internet connection' };
  }
}
